/**
 * Cart tracking: add_to_cart, remove_from_cart, view_cart.
 * Both CartAddEvent and CartUpdateEvent bubble `cart:update` to document.
 * CartAddEvent carries an empty resource, so we diff the AJAX cart against a
 * snapshot instead of trusting event payloads.
 */
import { track } from '@theme/analytics';
import { readJSON, fetchCart, cartLineToItem } from '@theme/analytics-utils';
import { applyAttribution } from '@theme/analytics-attribution';

/**
 * Build an item from a cart line, enriched with the list the product was
 * clicked from (item_list_name / item_list_id / index). /cart.js knows nothing
 * about lists, so the context comes from analytics-attribution.js.
 * @param {any} line
 */
function lineToItem(line) {
  return applyAttribution(cartLineToItem(line), line.product_id, line.variant_id);
}

/** @type {Map<number, number>} variant_id -> quantity (previous cycle) */
const snapshot = new Map();
/** @type {Map<number, Record<string, any>>} variant_id -> item (previous cycle, for removals) */
const itemCache = new Map();
let currency = '';
let busy = false;
let dirty = false;
/**
 * One-time snapshot seed, shared as a promise so an early `cart:update` can't
 * interleave with — or be clobbered by — the initial baseline fetch.
 * @type {Promise<void>|null}
 */
let seedPromise = null;

function seedOnce() {
  if (!seedPromise) {
    seedPromise = fetchCart().then((cart) => {
      if (cart) ingest(cart);
    });
  }
  return seedPromise;
}

/** Seed snapshot + cache from a cart. */
function ingest(cart) {
  snapshot.clear();
  itemCache.clear();
  currency = (cart && cart.currency) || currency;
  for (const line of (cart && cart.items) || []) {
    snapshot.set(line.variant_id, (snapshot.get(line.variant_id) || 0) + line.quantity);
    itemCache.set(line.variant_id, lineToItem(line));
  }
}


async function onCartChange() {
  if (busy) {
    // A change arrived while a fetch/diff was in flight — the in-flight pass may
    // have read an intermediate cart, so flag a re-run instead of dropping it.
    dirty = true;
    return;
  }
  busy = true;
  try {
    await seedOnce(); // baseline snapshot must exist before the first diff

    do {
      dirty = false;
      const cart = await fetchCart();
      if (!cart) break;
      currency = cart.currency || currency;

      const current = new Map();
      const currentItem = new Map();
      for (const line of cart.items || []) {
        current.set(line.variant_id, (current.get(line.variant_id) || 0) + line.quantity);
        currentItem.set(line.variant_id, lineToItem(line));
      }

      const variantIds = new Set([...snapshot.keys(), ...current.keys()]);
      for (const variantId of variantIds) {
        const delta = (current.get(variantId) || 0) - (snapshot.get(variantId) || 0);
        if (delta === 0) continue;
        const base = currentItem.get(variantId) || itemCache.get(variantId);
        if (!base) continue;
        const qty = Math.abs(delta);
        const item = { ...base, quantity: qty };
        const payload = { ecommerce: { value: (item.price || 0) * qty, currency, items: [item] } };
        track(delta > 0 ? 'add_to_cart' : 'remove_from_cart', payload);
      }

      snapshot.clear();
      itemCache.clear();
      for (const [k, v] of current) snapshot.set(k, v);
      for (const [k, v] of currentItem) itemCache.set(k, v);
    } while (dirty);
  } finally {
    busy = false;
  }
}

/** Fire view_cart from the current cart. */
async function fireViewCart() {
  const cart = await fetchCart();
  if (!cart) return;
  const items = (cart.items || []).map(lineToItem);
  track('view_cart', {
    ecommerce: {
      value: (cart.total_price || 0) / 100,
      currency: cart.currency || currency,
      items,
    },
  });
}

function init() {
  seedOnce(); // start seeding immediately; onCartChange awaits the same promise

  document.addEventListener('cart:update', onCartChange);

  const page = readJSON('analytics-page');
  if (page.page_type === 'cart') fireViewCart();

  const drawer = document.querySelector('cart-drawer-component');
  if (drawer) drawer.addEventListener('dialog:open', fireViewCart);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
